import { useEffect, useRef, useState, useCallback } from 'react';
import * as echarts from 'echarts';
import io from 'socket.io-client';
import { TrendingUp, TrendingDown, Radio } from 'lucide-react';
import { initialTree } from './treeData';
import {
  mergeChanges,
  collectMovers,
  rankGainersAndLosers,
  formatMoverPrice,
  formatMoverChange,
} from './movers';
import {
  findNodeByPath,
  navigateToMoverInPeerGroup,
} from './treePath';
import { normalizeQuoteUpdates } from './quoteMap';

export { navigateToMoverInPeerGroup } from './treePath';

const socket = io(window.location.origin, {
  path: '/pumpio/socket.io',
  transports: ['websocket', 'polling'],
});

const MOVER_LIMIT = 5;

/** Escape untrusted strings before interpolating into ECharts HTML tooltips. */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getColor(change = 0) {
  const clamped = Math.max(-6, Math.min(6, change));
  const hue = ((clamped + 6) / 12) * 120;
  const lightness = change === 0 ? 28 : 42 + Math.abs(clamped) * 2;
  return `hsl(${hue}, 78%, ${lightness}%)`;
}

function prepareData(node) {
  if (!node.children) {
    const change = node.change ?? 0;
    const sign = change > 0 ? '+' : '';
    return {
      name: node.name,
      value: node.value,
      change,
      price: node.price,
      itemStyle: { color: getColor(change) },
      label: {
        formatter: `{b}\n${sign}${change.toFixed(2)}%`,
        fontSize: 12,
        fontWeight: 600,
        color: '#f4f4f5',
        lineHeight: 16,
      },
    };
  }
  return {
    name: node.name,
    children: node.children.map(prepareData),
    itemStyle: { borderColor: '#18181b', borderWidth: 2, gapWidth: 2 },
  };
}

function getViewPathNames(viewRoot) {
  return viewRoot.getAncestors(true).map((node) => node.name);
}

function updateChartLeaves(chart, updates) {
  const seriesModel = chart.getModel().getSeriesByIndex(0);
  if (!seriesModel) return;

  const data = seriesModel.getData();
  const tree = data.tree;
  const viewRoot = seriesModel.getViewRoot();
  const viewPath = getViewPathNames(viewRoot);
  let changed = false;

  tree.root.eachNode({ attr: 'children', order: 'preorder' }, (node) => {
    if (node.children?.length) return;

    const ticker = node.name.split('\n')[0].trim();
    const quote = updates[ticker];
    if (!quote || quote.change === undefined) return;

    const change = quote.change;
    const sign = change > 0 ? '+' : '';
    const color = getColor(change);
    const rawItem = data.getRawDataItem(node.dataIndex);

    if (rawItem && typeof rawItem === 'object') {
      rawItem.change = change;
      if (quote.price !== undefined && quote.price !== null) rawItem.price = quote.price;
      rawItem.itemStyle = { ...(rawItem.itemStyle || {}), color };
      rawItem.label = {
        ...(rawItem.label || {}),
        formatter: `{b}\n${sign}${change.toFixed(2)}%`,
        fontSize: 12,
        fontWeight: 600,
        color: '#f4f4f5',
        lineHeight: 16,
      };
    }

    const style = node.getVisual('style') || {};
    node.setVisual('style', { ...style, fill: color });
    changed = true;
  });

  if (!changed) return;

  // Never call setOption here — ECharts treemap resets viewRoot to "US Market"
  // inside TreemapSeriesModel.optionUpdated() whenever series data is merged.
  chart.dispatchAction({
    type: 'treemapRender',
    seriesIndex: 0,
  });

  const currentViewRoot = seriesModel.getViewRoot();
  const treeRoot = tree.root;
  if (currentViewRoot === treeRoot && viewPath.length > 1) {
    const targetNode = findNodeByPath(treeRoot, viewPath);
    if (targetNode && targetNode !== treeRoot) {
      chart.dispatchAction({
        type: 'treemapZoomToNode',
        seriesIndex: 0,
        targetNode,
      });
    }
  }
}

function MoverRow({ mover, isGainer, onSelect }) {
  const priceLabel = formatMoverPrice(mover.price);
  const changeLabel = formatMoverChange(mover.change);
  const tone = isGainer
    ? 'bg-emerald-950/40 border-emerald-900/50 hover:border-emerald-700/60'
    : 'bg-rose-950/40 border-rose-900/50 hover:border-rose-700/60';
  const changeTone = isGainer ? 'text-emerald-400' : 'text-rose-400';

  return (
    <button
      type="button"
      onClick={() => onSelect(mover.name)}
      className={`w-full text-left px-3 py-2.5 rounded-lg flex justify-between items-center gap-2 border transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/60 ${tone}`}
      title={`Show ${mover.name} in its sector group`}
    >
      <span className="font-medium text-zinc-200 shrink-0">{mover.name}</span>
      <span className={`font-semibold flex items-center gap-1.5 text-sm tabular-nums ${changeTone}`}>
        {priceLabel ? (
          <span className="text-zinc-400 font-medium">{priceLabel}</span>
        ) : null}
        {isGainer ? <TrendingUp size={14} className="shrink-0" /> : <TrendingDown size={14} className="shrink-0" />}
        <span>{changeLabel}</span>
      </span>
    </button>
  );
}

export default function App() {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  // Eager tree so early marketUpdate (socket cache on connect) can merge quotes.
  const [treeData, setTreeData] = useState(() => structuredClone(initialTree));
  const treeDataRef = useRef(treeData);
  treeDataRef.current = treeData;
  const [gainers, setGainers] = useState([]);
  const [losers, setLosers] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [connected, setConnected] = useState(false);

  const buildChartOption = useCallback((data) => ({
    backgroundColor: 'transparent',
    tooltip: {
      backgroundColor: 'rgba(9,9,11,0.92)',
      borderColor: '#3f3f46',
      textStyle: { color: '#e4e4e7', fontSize: 13 },
      formatter: (params) => {
        const change = Number(params.data?.change) || 0;
        const sign = change > 0 ? '+' : '';
        const price = params.data?.price;
        const priceLine = price != null && Number.isFinite(Number(price))
          ? `$${Number(price).toFixed(2)}<br/>`
          : '';
        const safeName = escapeHtml(params.name);
        const mcap = Number(params.value);
        const mcapLabel = Number.isFinite(mcap) ? mcap : 0;
        return `<strong>${safeName}</strong><br/>${priceLine}$${mcapLabel}B market cap<br/><span style="color:${change >= 0 ? '#4ade80' : '#f87171'}">${sign}${change.toFixed(2)}%</span>`;
      },
    },
    series: [{
      type: 'treemap',
      data: [prepareData(data)],
      left: 0,
      right: 0,
      top: 4,
      bottom: 4,
      leafDepth: 2,
      roam: true,
      nodeClick: 'zoomToNode',
      breadcrumb: {
        show: true,
        height: 32,
        bottom: 8,
        itemStyle: {
          color: '#18181b',
          borderColor: '#3f3f46',
          shadowBlur: 0,
          textStyle: { color: '#a1a1aa' },
        },
        emphasis: { itemStyle: { color: '#27272a' } },
      },
      upperLabel: {
        show: true,
        height: 26,
        color: '#fafafa',
        fontWeight: 700,
        fontSize: 12,
      },
      label: {
        show: true,
        color: '#fafafa',
      },
      itemStyle: {
        borderColor: '#09090b',
        borderWidth: 2,
        gapWidth: 2,
      },
      levels: [
        {
          itemStyle: { borderColor: '#3f3f46', borderWidth: 3, gapWidth: 4 },
          upperLabel: { show: true, fontSize: 14 },
        },
        {
          itemStyle: { borderColor: '#27272a', gapWidth: 3 },
          upperLabel: { show: true },
        },
        {
          itemStyle: { borderColor: '#18181b', gapWidth: 2 },
          colorSaturation: [0.45, 0.75],
        },
      ],
      animationDuration: 500,
      animationDurationUpdate: 700,
      animationEasing: 'cubicOut',
    }],
  }), []);

  const handleMoverClick = useCallback((ticker) => {
    const chart = chartInstance.current;
    const data = treeDataRef.current;
    navigateToMoverInPeerGroup(chart, data, ticker);
  }, []);

  useEffect(() => {
    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);
    const onMarketUpdate = (raw) => {
      const updates = normalizeQuoteUpdates(raw);
      if (Object.keys(updates).length === 0) return;

      const prev = treeDataRef.current;
      if (!prev) return;
      const newTree = structuredClone(prev);
      mergeChanges(newTree, updates);
      const ranked = rankGainersAndLosers(collectMovers(newTree), MOVER_LIMIT);
      treeDataRef.current = newTree;
      setTreeData(newTree);
      setGainers(ranked.gainers);
      setLosers(ranked.losers);
      setLastUpdate(Date.now());

      if (chartInstance.current) {
        updateChartLeaves(chartInstance.current, updates);
      }
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('marketUpdate', onMarketUpdate);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('marketUpdate', onMarketUpdate);
    };
  }, []);

  useEffect(() => {
    if (!treeData || !chartRef.current || chartInstance.current) return;

    chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' });
    chartInstance.current.setOption(buildChartOption(treeData));
  }, [treeData, buildChartOption]);

  useEffect(() => {
    const onResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  const hasMovers = gainers.length > 0 || losers.length > 0;

  return (
    <div className="h-screen flex flex-col bg-[#09090b] text-zinc-300 overflow-hidden">
      <header className="px-5 py-4 flex items-center justify-between border-b border-zinc-800/80 bg-zinc-950/60 backdrop-blur-sm">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-1">PumpIO</p>
          <h1 className="text-2xl sm:text-4xl font-light tracking-tight bg-gradient-to-r from-sky-300 via-blue-400 to-indigo-400 bg-clip-text text-transparent">
            Live Market
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center" data-nguyenio-header-actions />
          <div className="text-right text-sm text-zinc-500">
            <div className="flex items-center justify-end gap-2">
              <Radio size={14} className={connected ? 'text-emerald-400' : 'text-zinc-600'} />
              <span className={connected ? 'text-emerald-400' : 'text-zinc-500'}>
                {connected ? 'Live' : 'Connecting…'}
              </span>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Left pane: gainers/losers + Updated footer (not in header next to nav) */}
        <aside className="w-72 shrink-0 flex flex-col border-r border-zinc-800/80 bg-zinc-950/40 min-h-0">
          <div className="flex-1 overflow-auto p-5 space-y-6 min-h-0">
            <section>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-400 mb-3 flex items-center gap-2">
                <TrendingUp size={16} className="text-emerald-400" /> Top Gainers
              </h2>
              <div className="space-y-2">
                {!hasMovers ? (
                  <p className="text-zinc-600 text-sm">Waiting for Finnhub data…</p>
                ) : gainers.length === 0 ? (
                  <p className="text-zinc-600 text-sm">No gainers yet</p>
                ) : (
                  gainers.map((m) => (
                    <MoverRow key={`g-${m.name}`} mover={m} isGainer onSelect={handleMoverClick} />
                  ))
                )}
              </div>
            </section>

            <section>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-400 mb-3 flex items-center gap-2">
                <TrendingDown size={16} className="text-rose-400" /> Top Losers
              </h2>
              <div className="space-y-2">
                {!hasMovers ? (
                  <p className="text-zinc-600 text-sm">Waiting for Finnhub data…</p>
                ) : losers.length === 0 ? (
                  <p className="text-zinc-600 text-sm">No losers yet</p>
                ) : (
                  losers.map((m) => (
                    <MoverRow key={`l-${m.name}`} mover={m} isGainer={false} onSelect={handleMoverClick} />
                  ))
                )}
              </div>
            </section>
          </div>

          <div
            className="shrink-0 px-5 py-3 border-t border-zinc-800/80 text-xs text-zinc-500 bg-zinc-950/80"
            data-pumpio-updated-footer
          >
            {lastUpdate ? `Updated ${new Date(lastUpdate).toLocaleTimeString()}` : 'Awaiting first quote…'}
          </div>
        </aside>

        <main className="flex-1 relative min-w-0">
          <div ref={chartRef} className="w-full h-full" />
          <div className="absolute bottom-5 right-5 flex items-center gap-3 px-4 py-2 rounded-full bg-zinc-950/80 border border-zinc-800 text-xs text-zinc-400 backdrop-blur-sm">
            <span className="text-rose-400 font-medium">-6%</span>
            <div
              className="w-28 h-2 rounded-full"
              style={{ background: 'linear-gradient(to right, hsl(0,78%,42%), hsl(60,30%,28%), hsl(120,78%,42%))' }}
            />
            <span className="text-emerald-400 font-medium">+6%</span>
          </div>
        </main>
      </div>
    </div>
  );
}
